import { FC, ReactNode, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
  SilentRequest,
} from "@azure/msal-browser";
import { JwtUtil } from "shared-ts-utils-core";

export interface AccessTokenProviderProps {
  method: InteractionType;
  clientId: string;
  scopes: Array<string>;
  children: (accessToken: string) => ReactNode;
}

export const MsalAccessTokenProvider: FC<AccessTokenProviderProps> = (
  props
) => {
  const { method, clientId, scopes } = props;
  const { instance, inProgress, accounts } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessTokenExpiry, setAccessTokenExpiry] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (accessTokenExpiry != null) {
      const expiryMS = accessTokenExpiry * 1000;
      const expiryBuffer = 5 * 60 * 1000;
      const timeout = expiryMS - Date.now() - expiryBuffer;
      console.log("Token expires " + accessTokenExpiry);
      console.log("Refresh token in " + timeout / (60 * 1000.0) + " min");
      if (timeout > 0) {
        const timer = setTimeout(() => {
          setAccessToken(null);
          setAccessTokenExpiry(null);
        }, timeout);
        return () => clearTimeout(timer);
      }
    }
  }, [accessTokenExpiry]);

  useEffect(() => {
    if (accessToken === null && inProgress === InteractionStatus.None) {
      if (accounts.length > 0) {
        const accessTokenRequest: SilentRequest = {
          scopes: scopes,
          account: accounts[0],
        };
        const lookupAccessToken = async () => {
          try {
            instance.acquireTokenSilent(accessTokenRequest).then((result) => {
              //console.log("Token acquired silently: " + result.accessToken);

              const decodedToken = JwtUtil.decode(result.accessToken);
              console.log("Decoded Token:", decodedToken);
              setAccessToken(result.accessToken);
              setAccessTokenExpiry(decodedToken.exp);
            });
          } catch (error) {
            // acquireTokenSilent can fail for a number of reasons, fallback to interaction
            if (error instanceof InteractionRequiredAuthError) {
              if (method === InteractionType.Redirect) {
                instance
                  .acquireTokenRedirect(accessTokenRequest)
                  .catch((error) => {
                    console.error("Failed to acquireTokenRedirect", error);
                    throw error;
                  });
              } else {
                instance
                  .acquireTokenPopup(accessTokenRequest)
                  .then((result) => {
                    setAccessToken(result.accessToken);
                  })
                  .catch((error) => {
                    console.error("Failed to acquireTokenPopup", error);
                    throw error;
                  });
              }
            } else {
              console.error("Failed to acquireTokenSilent", error);
              throw error;
            }
          }
        };

        lookupAccessToken().then();
      } else {
        console.warn("Msal accounts is empty");
      }
    }
  }, [accounts, inProgress, instance, accessToken, clientId, scopes, method]);

  if (accessToken == null) {
    return null;
  }

  return <>{props.children(accessToken)}</>;
};
